"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const constants_1 = require("@framework/constants");
const context_1 = require("./context");
const error_1 = require("./error");
const fetch_1 = require("./fetch");
const utils_1 = require("./utils");
const ApiProvider = props => {
    const { baseUrl, getAuthToken = () => "", refreshToken = () => Promise.resolve(), storageName = constants_1.ns, children, } = props;
    const { fetchJson } = (0, fetch_1.useFetchJson)();
    const { fetchFile } = (0, fetch_1.useFetchFile)();
    const { fetchBlob } = (0, fetch_1.useFetchBlob)();
    const { fetchBlobWithFileName } = (0, fetch_1.useFetchBlobWithFileName)();
    const isTokenRefreshing = (0, react_1.useRef)(false);
    const refreshTokenPromise = (0, react_1.useRef)(null);
    const isHandling403Error = (0, react_1.useRef)(false);
    const initializeProvider = (0, react_1.useCallback)(() => {
        (0, utils_1.setBaseUrl)(baseUrl);
        storageName && (0, utils_1.setStorageName)(`jwt_${storageName}`);
    }, [baseUrl, storageName]);
    initializeProvider();
    const handle403Error = (0, react_1.useCallback)((storageName) => {
        if (isHandling403Error.current) {
            return null;
        }
        isHandling403Error.current = true;
        localStorage.removeItem(`jwt_${storageName}`);
        (0, utils_1.setToken)(null);
        window.dispatchEvent(new CustomEvent("session_expired"));
        setTimeout(() => {
            isHandling403Error.current = false;
        }, 1000);
        return null;
    }, []);
    const fetchJsonWithInterceptor = (0, react_1.useCallback)(async (input, init) => {
        try {
            let response = await fetchJson(input, init);
            if (response === null) {
                return response;
            }
            if (response.status === 401) {
                if (!isTokenRefreshing.current) {
                    isTokenRefreshing.current = true;
                    refreshTokenPromise.current = refreshToken().finally(() => {
                        isTokenRefreshing.current = false;
                        refreshTokenPromise.current = null;
                    });
                    await refreshTokenPromise.current;
                }
                else {
                    await refreshTokenPromise.current;
                }
                const newToken = getAuthToken();
                if (newToken) {
                    (init === null || init === void 0 ? void 0 : init.headers).set("Authorization", `Bearer ${newToken}`);
                    response = await fetchJson(input, init);
                }
                else {
                    localStorage.removeItem(`jwt_${storageName}`);
                    window.dispatchEvent(new CustomEvent("session_expired"));
                    return null;
                }
            }
            return response;
        }
        catch (error) {
            if (error instanceof error_1.ApiError && (error === null || error === void 0 ? void 0 : error.status) === 403) {
                const requestUrl = typeof input === "string" ? input : input.url;
                const isGuestAuthEndpoint = (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/login")) ||
                    (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/genpk")) ||
                    (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/set-new-password")) ||
                    (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/check-password-policy")) ||
                    (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/plc"));
                if (isGuestAuthEndpoint) {
                    throw error;
                }
                return handle403Error(storageName);
            }
            throw error;
        }
    }, [fetchJson, refreshToken, getAuthToken, handle403Error, storageName]);
    const fetchBlobWithInterceptor = (0, react_1.useCallback)(async (input, init) => {
        try {
            let response = await fetchBlob(input, init);
            if (response === null) {
                return response;
            }
            if (response.status === 401) {
                if (!isTokenRefreshing.current) {
                    isTokenRefreshing.current = true;
                    refreshTokenPromise.current = refreshToken().finally(() => {
                        isTokenRefreshing.current = false;
                        refreshTokenPromise.current = null;
                    });
                    await refreshTokenPromise.current;
                }
                else {
                    await refreshTokenPromise.current;
                }
                const newToken = getAuthToken();
                if (newToken) {
                    (init === null || init === void 0 ? void 0 : init.headers).set("Authorization", `Bearer ${newToken}`);
                    response = await fetchBlob(input, init);
                }
                else {
                    localStorage.removeItem(`jwt_${storageName}`);
                    window.dispatchEvent(new CustomEvent("session_expired"));
                    return null;
                }
            }
            return response;
        }
        catch (error) {
            if (error instanceof error_1.ApiError && (error === null || error === void 0 ? void 0 : error.status) === 403) {
                const requestUrl = typeof input === "string" ? input : input.url;
                const isGuestAuthEndpoint = (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/login")) ||
                    (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/genpk")) ||
                    (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/set-new-password")) ||
                    (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/check-password-policy")) ||
                    (requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.includes("/auth/plc"));
                if (isGuestAuthEndpoint) {
                    throw error;
                }
                return handle403Error(storageName);
            }
            throw error;
        }
    }, [fetchBlob, refreshToken, getAuthToken, handle403Error, storageName]);
    const contextValue = (0, react_1.useMemo)(() => ({
        fetchJson: (0, utils_1.prepare)({ fetch: fetchJsonWithInterceptor, getAuthToken }),
        fetchFile: (0, utils_1.prepare)({ fetch: fetchFile, getAuthToken }),
        fetchBlob: (0, utils_1.prepare)({ fetch: fetchBlobWithInterceptor, getAuthToken }),
        fetchBlobWithFileName: (0, utils_1.prepare)({ fetch: fetchBlobWithFileName, getAuthToken }),
        getToken: utils_1.getToken,
        setToken: utils_1.setToken,
        refreshToken,
    }), [
        fetchJsonWithInterceptor,
        fetchFile,
        fetchBlobWithInterceptor,
        fetchBlobWithFileName,
        getAuthToken,
        utils_1.getToken,
        utils_1.setToken,
        refreshToken,
    ]);
    return (0, jsx_runtime_1.jsx)(context_1.ApiContext.Provider, { value: contextValue, children: children });
};
exports.ApiProvider = ApiProvider;
//# sourceMappingURL=provider.js.map